import React, { useState } from 'react';
import {
  useIngredientes,
  useCriarIngrediente,
  useAtualizarIngrediente,
  useRemoverIngrediente,
  useRegistrarMovimentacao,
} from '../../hooks/useIngredientes';
import { useUnidadesMedida } from '../../hooks/useUnidadesMedida';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Ingrediente, CriarIngredienteRequest, TipoMovimentacao } from '../../types';
import Button from '../../components/Common/Button';
import ConfirmModal from '../../components/Common/ConfirmModal';

type ModalType = 'criar' | 'editar' | 'movimentacao' | null;

const Estoque: React.FC = () => {
  usePageTitle('Estoque');
  const { data: ingredientes, isLoading } = useIngredientes();
  const { data: unidadesMedida, isLoading: carregandoUnidades } = useUnidadesMedida();
  const { mutate: criarIngrediente, isPending: criando } = useCriarIngrediente();
  const { mutate: atualizarIngrediente, isPending: atualizando } = useAtualizarIngrediente();
  const { mutate: removerIngrediente, isPending: removendo } = useRemoverIngrediente();
  const { mutate: registrarMovimentacao, isPending: registrandoMovimentacao } = useRegistrarMovimentacao();

  const [modalAberto, setModalAberto] = useState<ModalType>(null);
  const [ingredienteSelecionado, setIngredienteSelecionado] = useState<Ingrediente | null>(null);
  const [displayValues, setDisplayValues] = useState({ quantidadeInicial: '', limiarAlerta: '' });
  const [movDisplay, setMovDisplay] = useState('');

  const [confirmarRemocao, setConfirmarRemocao] = useState<{
    isOpen: boolean;
    ingredienteId: number | null;
    ingredienteNome: string;
  }>({
    isOpen: false,
    ingredienteId: null,
    ingredienteNome: '',
  });

  const [formData, setFormData] = useState<CriarIngredienteRequest>({
    nome: '',
    quantidadeInicial: 0,
    unidadeMedidaId: 0,
    limiarAlerta: 0,
  });

  const [movimentacaoData, setMovimentacaoData] = useState({
    tipo: TipoMovimentacao.Entrada,
    quantidade: 0,
    motivo: '',
  });

  const abrirModalCriar = () => {
    setFormData({
      nome: '',
      quantidadeInicial: 0,
      unidadeMedidaId: 0,
      limiarAlerta: 0,
    });
    setDisplayValues({ quantidadeInicial: '', limiarAlerta: '' });
    setModalAberto('criar');
  };

  const abrirModalEditar = (ingrediente: Ingrediente) => {
    setIngredienteSelecionado(ingrediente);
    setFormData({
      nome: ingrediente.nome,
      quantidadeInicial: ingrediente.quantidadeEstoque,
      unidadeMedidaId: ingrediente.unidadeMedidaId,
      limiarAlerta: ingrediente.limiarAlerta,
    });
    setDisplayValues({
      quantidadeInicial: String(ingrediente.quantidadeEstoque),
      limiarAlerta: String(ingrediente.limiarAlerta),
    });
    setModalAberto('editar');
  };

  const abrirModalMovimentacao = (ingrediente: Ingrediente) => {
    setIngredienteSelecionado(ingrediente);
    setMovimentacaoData({
      tipo: TipoMovimentacao.Entrada,
      quantidade: 0,
      motivo: '',
    });
    setMovDisplay('');
    setModalAberto('movimentacao');
  };

  const fecharModal = () => {
    setModalAberto(null);
    setIngredienteSelecionado(null);
    setFormData({
      nome: '',
      quantidadeInicial: 0,
      unidadeMedidaId: 0,
      limiarAlerta: 0,
    });
    setMovimentacaoData({
      tipo: TipoMovimentacao.Entrada,
      quantidade: 0,
      motivo: '',
    });
    setDisplayValues({ quantidadeInicial: '', limiarAlerta: '' });
    setMovDisplay('');
  };

  const handleSubmitCriar = (e: React.FormEvent) => {
    e.preventDefault();
    criarIngrediente(formData, {
      onSuccess: () => fecharModal(),
    });
  };

  const handleSubmitEditar = (e: React.FormEvent) => {
    e.preventDefault();
    if (ingredienteSelecionado) {
      atualizarIngrediente(
        { id: ingredienteSelecionado.id, data: formData },
        {
          onSuccess: () => fecharModal(),
        }
      );
    }
  };

  const handleSubmitMovimentacao = (e: React.FormEvent) => {
    e.preventDefault();
    if (ingredienteSelecionado) {
      registrarMovimentacao(
        {
          ingredienteId: ingredienteSelecionado.id,
          tipo: movimentacaoData.tipo,
          quantidade: movimentacaoData.quantidade,
          motivo: movimentacaoData.motivo || undefined,
        },
        {
          onSuccess: () => fecharModal(),
        }
      );
    }
  };

  const handleRemoverClick = (ingrediente: Ingrediente) => {
    setConfirmarRemocao({
      isOpen: true,
      ingredienteId: ingrediente.id,
      ingredienteNome: ingrediente.nome,
    });
  };

  const confirmarRemocaoIngrediente = () => {
    if (confirmarRemocao.ingredienteId) {
      removerIngrediente(confirmarRemocao.ingredienteId);
    }
    setConfirmarRemocao({
      isOpen: false,
      ingredienteId: null,
      ingredienteNome: '',
    });
  };

  const cancelarRemocao = () => {
    setConfirmarRemocao({
      isOpen: false,
      ingredienteId: null,
      ingredienteNome: '',
    });
  };

  const getEstoqueBadgeClass = (quantidade: number, minimo: number) => {
    if (quantidade <= 0) return 'bg-red-100 text-red-800';
    if (quantidade <= minimo) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Gestão de Estoque</h1>
        <Button onClick={abrirModalCriar} variant="primary" size="md">
          Adicionar Ingrediente
        </Button>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Carregando ingredientes...</p>
        </div>
      ) : ingredientes && ingredientes.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ingrediente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estoque Atual
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unidade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estoque Mínimo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ingredientes.map((ingrediente) => (
                  <tr key={ingrediente.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{ingrediente.nome}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {ingrediente.quantidadeEstoque}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{ingrediente.unidadeMedidaSigla}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{ingrediente.limiarAlerta}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getEstoqueBadgeClass(
                          ingrediente.quantidadeEstoque,
                          ingrediente.limiarAlerta
                        )}`}
                      >
                        {ingrediente.quantidadeEstoque <= 0
                          ? 'Sem Estoque'
                          : ingrediente.quantidadeEstoque <= ingrediente.limiarAlerta
                          ? 'Baixo'
                          : 'Normal'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => abrirModalMovimentacao(ingrediente)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Movimentar
                        </button>
                        <button
                          onClick={() => abrirModalEditar(ingrediente)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleRemoverClick(ingrediente)}
                          className="text-red-600 hover:text-red-900"
                          disabled={removendo}
                        >
                          Remover
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Nenhum ingrediente cadastrado</p>
        </div>
      )}

      {/* Modal Criar/Editar Ingrediente */}
      {(modalAberto === 'criar' || modalAberto === 'editar') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={fecharModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {modalAberto === 'criar' ? 'Adicionar Ingrediente' : 'Editar Ingrediente'}
            </h2>
            <form onSubmit={modalAberto === 'criar' ? handleSubmitCriar : handleSubmitEditar}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Ingrediente
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantidade Inicial
                  </label>
                  <input
                    type="text"
                    value={displayValues.quantidadeInicial}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9.,]/g, '');
                      setDisplayValues(prev => ({ ...prev, quantidadeInicial: raw }));
                      setFormData({ ...formData, quantidadeInicial: parseFloat(raw.replace(',', '.')) || 0 });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unidade de Medida
                  </label>
                  <select
                    value={formData.unidadeMedidaId}
                    onChange={(e) => setFormData({ ...formData, unidadeMedidaId: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={carregandoUnidades}
                  >
                    <option value={0}>Selecione uma unidade</option>
                    {unidadesMedida?.map((unidade) => (
                      <option key={unidade.id} value={unidade.id}>
                        {unidade.sigla} - {unidade.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estoque Mínimo
                  </label>
                  <input
                    type="text"
                    value={displayValues.limiarAlerta}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9.,]/g, '');
                      setDisplayValues(prev => ({ ...prev, limiarAlerta: raw }));
                      setFormData({ ...formData, limiarAlerta: parseFloat(raw.replace(',', '.')) || 0 });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  onClick={fecharModal}
                  variant="secondary"
                  size="md"
                  fullWidth
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  disabled={criando || atualizando}
                >
                  {modalAberto === 'criar'
                    ? criando
                      ? 'Criando...'
                      : 'Criar'
                    : atualizando
                    ? 'Salvando...'
                    : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Movimentação */}
      {modalAberto === 'movimentacao' && ingredienteSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={fecharModal}
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Movimentar Estoque - {ingredienteSelecionado.nome}
            </h2>
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Estoque Atual:</p>
              <p className="text-lg font-semibold text-gray-900">
                {ingredienteSelecionado.quantidadeEstoque} {ingredienteSelecionado.unidadeMedidaSigla}
              </p>
            </div>
            <form onSubmit={handleSubmitMovimentacao}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Movimentação
                  </label>
                  <select
                    value={movimentacaoData.tipo}
                    onChange={(e) =>
                      setMovimentacaoData({
                        ...movimentacaoData,
                        tipo: e.target.value as TipoMovimentacao,
                      })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={TipoMovimentacao.Entrada}>Entrada</option>
                    <option value={TipoMovimentacao.Saida}>Saída</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantidade
                  </label>
                  <input
                    type="text"
                    value={movDisplay}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9.,]/g, '');
                      setMovDisplay(raw);
                      setMovimentacaoData({
                        ...movimentacaoData,
                        quantidade: parseFloat(raw.replace(',', '.')) || 0,
                      });
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo (opcional)
                  </label>
                  <textarea
                    value={movimentacaoData.motivo}
                    onChange={(e) =>
                      setMovimentacaoData({ ...movimentacaoData, motivo: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Descreva o motivo da movimentação..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  onClick={fecharModal}
                  variant="secondary"
                  size="md"
                  fullWidth
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  disabled={registrandoMovimentacao}
                >
                  {registrandoMovimentacao ? 'Registrando...' : 'Registrar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmarRemocao.isOpen}
        title="Confirmar Remoção"
        message={`Deseja remover o ingrediente "${confirmarRemocao.ingredienteNome}"? Esta ação não pode ser desfeita.`}
        confirmText="Remover"
        cancelText="Cancelar"
        onConfirm={confirmarRemocaoIngrediente}
        onCancel={cancelarRemocao}
      />
    </div>
  );
};

export default Estoque;
